import { Box, VStack, Text, Input, Button } from '@chakra-ui/react';
import { File, X, UploadSimple, CheckCircle } from '@phosphor-icons/react';
import { getFileSize } from '@/utils';
import { MAX_RESUME_FILE_SIZE } from '@/config/constants';

const FILE_SIZE_LIMIT = MAX_RESUME_FILE_SIZE;

interface FileUploaderProps {
  id: string;
  required?: boolean;
  label: string;
  file: File | null;
  error: string | null;
  uploading?: boolean;
  supportedFileTypes?: string[];
  onFileSelect: (file: File | null, error: string | null) => void;
  onFileRemove: () => void;
}

export default function FileUploader({
  id,
  required = false,
  label,
  file,
  error,
  uploading = false,
  supportedFileTypes = ['.pdf', '.txt'],
  onFileSelect,
  onFileRemove
}: FileUploaderProps) {
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      // Validate file type
      const fileExtension = selectedFile.name
        .toLowerCase()
        .slice(selectedFile.name.lastIndexOf('.'));

      if (!supportedFileTypes.includes(fileExtension)) {
        onFileSelect(
          null,
          `Unsupported file type. Supported types: ${supportedFileTypes.join(', ')}`
        );
        return;
      }

      // Validate file size
      if (selectedFile.size > FILE_SIZE_LIMIT) {
        onFileSelect(null, `File size exceeds the limit of ${getFileSize(FILE_SIZE_LIMIT)}.`);
        return;
      }

      onFileSelect(selectedFile, null);
    }
  };

  return (
    <VStack alignItems="flex-start" w="100%">
      <Text fontSize="18px" fontWeight="bold" color="neutrals.800">
        {required && (
          <Text as="span" color="error.700" mr="4px" fontWeight="bold">
            *
          </Text>
        )}
        {label}
        {!required && (
          <Text as="span" color="neutrals.500" fontWeight="normal">
            {' '}
            (optional)
          </Text>
        )}
      </Text>
      <Box position="relative" w="100%" h={{ base: '200px', md: '240px' }} alignContent="center">
        {file ? (
          <Box
            border="1px solid"
            borderColor={uploading ? 'neutrals.300' : error ? 'error.500' : 'success.500'}
            borderRadius="lg"
            p={{ base: '12px 16px', md: '16px 32px' }}
            h={{ base: '200px', md: '240px' }}
            w={{ base: '100%', md: '420px' }}
            maxW={{ base: '100%', md: '420px' }}
            textAlign="center"
            bg="neutrals.50"
            cursor={uploading ? 'not-allowed' : 'pointer'}
            transition="all 0.2s"
          >
            <VStack alignItems="center" alignContent="center" gap="24px">
              {!uploading && (
                <Button
                  variant="ghost"
                  onClick={onFileRemove}
                  position="absolute"
                  top="16px"
                  right="16px"
                >
                  <X size={16} color="#545454" />
                </Button>
              )}
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width={{ base: '80px', md: '100px' }}
                height={{ base: '80px', md: '100px' }}
                borderRadius="12px"
                border="1px solid"
                borderColor={error ? 'error.500' : 'primary.100'}
                bg={uploading ? 'neutrals.100' : 'success.100'}
              >
                <Box color={uploading ? 'neutrals.500' : 'success.500'}>
                  <File size={40} />
                </Box>
              </Box>
              <Box color="neutrals.900">
                <Box display="flex" alignItems="center" mb="4px">
                  {!uploading && (
                    <Box mr="12px" color="success.500">
                      <CheckCircle size={24} weight="fill" />
                    </Box>
                  )}
                  <Box
                    fontSize={{ base: '14px', md: '16px' }}
                    color={uploading ? 'neutrals.500' : 'neutrals.800'}
                    mr="16px"
                    maxW={{ base: '200px', md: '320px' }}
                    whiteSpace="nowrap"
                    overflow="clip"
                    textOverflow="ellipsis"
                  >
                    {file.name}
                  </Box>
                </Box>
                <Box
                  fontSize={{ base: '14px', md: '16px' }}
                  color={error ? 'error.700' : 'neutrals.500'}
                >
                  {file.size ? getFileSize(file.size) : '0 Bytes'}
                </Box>
                {error && (
                  <Box fontSize={{ base: '14px', md: '16px' }} color="error.700" mt="4px">
                    {error}
                  </Box>
                )}
              </Box>
            </VStack>
          </Box>
        ) : (
          <Box
            border="2px dashed"
            borderColor={error ? 'error.500' : 'neutrals.300'}
            borderRadius="lg"
            p={{ base: '12px 16px', md: '16px 32px' }}
            h={{ base: '200px', md: '240px' }}
            w={{ base: '100%', md: '420px' }}
            maxW={{ base: '100%', md: '420px' }}
            textAlign="center"
            bg={file ? 'success.50' : 'neutrals.50'}
            transition="all 0.2s"
            opacity={uploading ? 0.6 : 1}
            cursor={uploading ? 'not-allowed' : 'pointer'}
          >
            <Input
              id={id}
              type="file"
              accept={supportedFileTypes.join(',')}
              onChange={handleFileSelect}
              position="absolute"
              opacity={0}
              w="full"
              h="full"
              cursor={uploading ? 'not-allowed' : 'pointer'}
              top={0}
              left={0}
              disabled={uploading}
            />
            <VStack
              gap={{ base: '6px', md: '8px' }}
              py={{ base: '16px', md: '20px' }}
              align="center"
            >
              <UploadSimple size={60} color="neutrals.700" />
              <VStack gap="0" align="center">
                <Box
                  textAlign="center"
                  color="neutrals.900"
                  mt={{ base: '16px', md: '20px' }}
                  fontSize={{ base: '14px', md: '16px' }}
                >
                  Drop files here or{' '}
                  <Box as="span" color="primary.900" fontWeight="medium">
                    click to upload
                  </Box>
                </Box>
                <Box color="neutrals.500" fontSize={{ base: '14px', md: '16px' }}>
                  Formats: {supportedFileTypes.join(', ')} (max {getFileSize(FILE_SIZE_LIMIT)})
                </Box>
                {error && (
                  <Box fontSize={{ base: '14px', md: '16px' }} color="error.700">
                    {error}
                  </Box>
                )}
              </VStack>
            </VStack>
          </Box>
        )}
      </Box>
    </VStack>
  );
}
